import { useMemo } from 'react'
import { Link } from '@/lib/router'
import type { ProjectListItem } from '../../api/types'
import { formatCrore } from '../../lib/format'
import { squarify, type Rect } from '../../lib/squarify'
import { groupByCategory } from './chartData'
import Tooltip from './Tooltip'
import { useElementWidth } from './useElementWidth'
import { useTooltip } from './useTooltip'

const HEIGHT = 380
const HEADER = 18 // room for the category name above its blocks
const GAP = 1 // each block is inset 1px, giving a 2px gap between neighbours

/**
 * "Where the value sits": each block is a project, its area is its ₹ value, its colour is its stage.
 * Blocks are grouped by category. Clicking a block opens the project.
 */
export default function Treemap({ projects }: { projects: ProjectListItem[] }) {
  const { ref: sizeRef, width } = useElementWidth<HTMLDivElement>()
  const { ref: tipRef, tip, bind } = useTooltip<HTMLDivElement>()

  const layout = useMemo(() => {
    if (width === 0) return []
    const groups = groupByCategory(projects)
    return squarify(
      groups.map((g) => ({ value: g.value, item: g })),
      { x: 0, y: 0, w: width, h: HEIGHT },
    ).map(({ item: group, rect }) => {
      const inner: Rect = { x: rect.x, y: rect.y + HEADER, w: rect.w, h: Math.max(rect.h - HEADER, 0) }
      const blocks = squarify(
        group.projects.map((p) => ({ value: p.valueRupees, item: p })),
        inner,
      )
      return { group, rect, blocks }
    })
  }, [projects, width])

  return (
    <div ref={sizeRef}>
      <div className="treemap" ref={tipRef} style={{ height: HEIGHT }}>
        {layout.map(({ group, rect, blocks }) => (
          <div key={group.category}>
            <div
              className="tm-header"
              style={{ left: rect.x + GAP, top: rect.y, width: Math.max(rect.w - 2 * GAP, 0) }}
              title={`${group.category} · ${formatCrore(group.value)}`}
            >
              {rect.w > 70 && `${group.category} · ${formatCrore(group.value)}`}
            </div>
            {blocks.map(({ item: p, rect: r }) => {
              const w = r.w - 2 * GAP
              const h = r.h - 2 * GAP
              const showTitle = w > 70 && h > 30
              const showValue = h > 46
              // As many title lines as the block's height allows (the full name is in the hover tip).
              const titleLines = Math.max(1, Math.floor((h - 10 - (showValue ? 16 : 0)) / 14))
              return (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className={`tm-block stage-${p.stage.toLowerCase()}`}
                  style={{ left: r.x + GAP, top: r.y + GAP, width: w, height: h }}
                  {...bind(`${formatCrore(p.valueRupees)} · ${p.stage}`, p.title)}
                >
                  {showTitle && (
                    <>
                      <span className="tm-title" style={{ WebkitLineClamp: titleLines }}>{p.title}</span>
                      {showValue && <span className="tm-value">{formatCrore(p.valueRupees)}</span>}
                    </>
                  )}
                </Link>
              )
            })}
          </div>
        ))}
        <Tooltip tip={tip} />
      </div>
      <div className="chart-legend">
        <span><i className="swatch stage-lead" /> Lead</span>
        <span><i className="swatch stage-opportunity" /> Opportunity</span>
        <span><i className="swatch stage-tender" /> Tender</span>
        <span className="muted">Area = ₹ value</span>
      </div>
    </div>
  )
}
